import React, { useCallback, useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const MAX_LINES = 2;

interface RenderNamesCellProps<T> {
    items?: T[];
    colorClass?: string;
    getKey: (item: T) => string | number;
    formatName: (item: T) => string;
    renderItem?: (item: T) => React.ReactNode;
}

const RenderNamesCell = <T,>({ items, colorClass = "", getKey, formatName, renderItem }: RenderNamesCellProps<T>) => {
    const textRef = useRef<HTMLSpanElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const checkOverflow = useCallback(() => {
        if (!textRef.current) return;

        const element = textRef.current;
        const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
        const maxHeight = Math.ceil(lineHeight * MAX_LINES);

        setIsOverflowing(Math.ceil(element.scrollHeight) > maxHeight);
    }, []);

    useEffect(() => {
        if (!textRef.current) return;

        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(textRef.current);

        const timeoutId = setTimeout(checkOverflow, 100);

        return () => {
            resizeObserver.disconnect();
            clearTimeout(timeoutId);
        };
    }, [checkOverflow, items]);

    if (!items?.length) return null;

    const inlineContent = renderItem
        ? items.map((item, index) => (
              <React.Fragment key={getKey(item)}>
                  {index > 0 ? "; " : null}
                  {renderItem(item)}
              </React.Fragment>
          ))
        : items.map(formatName).join("; ");

    const inner = (
        <span ref={textRef} className="supplements-cell">
            {inlineContent}
        </span>
    );

    const content = <span className={`d-inline-block mw-100 ${colorClass}`}>{inner}</span>;

    if (!isOverflowing) {
        return content;
    }

    const tooltip = (
        <Tooltip id="render-names-tooltip">
            <div className="text-start">
                {items.map((item) => (
                    <div key={getKey(item)}>{renderItem ? renderItem(item) : formatName(item)}</div>
                ))}
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger placement="top" overlay={tooltip} delay={{ show: 200, hide: 0 }}>
            {content}
        </OverlayTrigger>
    );
};

export default RenderNamesCell;
